import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, Screen, Text } from "@andrum/ui";
import {
  fetchBillingStatus,
  startCheckout,
  type BillingStatus,
  type PaywallReason,
} from "@/features/subscription/api/billing-api";
import {
  getRevenueCatOfferings,
  purchaseRevenueCatPlan,
  restoreRevenueCatPurchases,
  type RevenueCatOfferingSummary,
  type RcPlanSlot,
} from "@/lib/billing/revenuecat";
import { clearEntitlementCache } from "@/lib/billing/entitlements";
import { isNative } from "@/lib/capacitor/platform";
import { FREE_COACH_MESSAGES_PER_DAY } from "@/lib/billing/premium-gate";

function parseReason(raw: unknown): PaywallReason {
  if (
    raw === "coach" ||
    raw === "experiment" ||
    raw === "programs" ||
    raw === "explore"
  ) {
    return raw;
  }
  return "default";
}

export function PaywallScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    reason?: string;
    checkout?: string;
  };
  const reason = parseReason(search.reason);

  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [rcOffering, setRcOffering] = useState<RevenueCatOfferingSummary | null>(
    null
  );
  const [selected, setSelected] = useState<RcPlanSlot>("plus_yearly");

  const useRevenueCat = isNative && Boolean(rcOffering?.monthly || rcOffering?.yearly);

  useEffect(() => {
    void fetchBillingStatus().then(setStatus);
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      setMessage(t("paywall.success"));
      clearEntitlementCache();
    }
    if (params.get("checkout") === "cancel") {
      setMessage(t("paywall.cancel"));
    }
    if (isNative) {
      void getRevenueCatOfferings().then((offering) => {
        setRcOffering(offering);
        // Prefer yearly when available
        if (offering?.yearly) setSelected("plus_yearly");
        else if (offering?.monthly) setSelected("plus_monthly");
      });
    }
  }, [t]);

  const hero = useMemo(() => {
    switch (reason) {
      case "coach":
        return {
          title: t("paywall.reason.coachTitle"),
          lead: t("paywall.reason.coachLead", {
            n: FREE_COACH_MESSAGES_PER_DAY,
          }),
        };
      case "experiment":
        return {
          title: t("paywall.reason.experimentTitle"),
          lead: t("paywall.reason.experimentLead"),
        };
      case "programs":
        return {
          title: t("paywall.reason.programsTitle"),
          lead: t("paywall.reason.programsLead"),
        };
      case "explore":
        return {
          title: t("paywall.reason.exploreTitle"),
          lead: t("paywall.reason.exploreLead"),
        };
      default:
        return {
          title: t("paywall.title"),
          lead: t("paywall.lead"),
        };
    }
  }, [reason, t]);

  const monthlyLabel = useRevenueCat
    ? (rcOffering?.monthly?.priceString || t("paywall.priceMonthlyFallback"))
    : (status?.prices?.monthly?.label ?? t("paywall.priceMonthlyFallback"));
  const yearlyLabel = useRevenueCat
    ? (rcOffering?.yearly?.priceString || t("paywall.priceYearlyFallback"))
    : (status?.prices?.yearly?.label ?? t("paywall.priceYearlyFallback"));

  const savePct = useMemo(() => {
    if (useRevenueCat && rcOffering?.monthly && rcOffering?.yearly) {
      const m = rcOffering.monthly.price;
      const y = rcOffering.yearly.price;
      if (m > 0 && y > 0 && m * 12 > y) {
        return Math.round(((m * 12 - y) / (m * 12)) * 100);
      }
      return null;
    }
    return status?.prices?.yearlySavePercent ?? null;
  }, [useRevenueCat, rcOffering, status?.prices?.yearlySavePercent]);

  const purchase = async (plan: RcPlanSlot) => {
    setBusy(true);
    setMessage(null);

    if (useRevenueCat) {
      const result = await purchaseRevenueCatPlan(plan);
      setBusy(false);
      if (!result.ok) {
        setMessage(result.message);
        return;
      }
      clearEntitlementCache();
      setMessage(
        result.customer.hasPlus
          ? t("paywall.nativeOk")
          : t("paywall.nativePending")
      );
      void fetchBillingStatus().then(setStatus);
      return;
    }

    // Web → Stripe Checkout
    const result = await startCheckout(plan);
    setBusy(false);
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    window.location.href = result.url;
  };

  const restore = async () => {
    setBusy(true);
    const result = await restoreRevenueCatPurchases();
    setBusy(false);
    clearEntitlementCache();
    if (result.ok && result.customer?.hasPlus) {
      setMessage(t("paywall.restoreOk"));
    } else if (result.ok) {
      setMessage(t("paywall.restoreNone"));
    } else {
      setMessage(t("paywall.restoreNone"));
    }
    void fetchBillingStatus().then(setStatus);
  };

  const canPurchase =
    useRevenueCat
      ? (selected === "plus_yearly" ? Boolean(rcOffering?.yearly) : Boolean(rcOffering?.monthly))
      : Boolean(status?.checkoutAvailable);

  const ctaLabel = useRevenueCat
    ? t("paywall.ctaBuy")
    : status?.checkoutAvailable
      ? t("paywall.ctaContinue")
      : t("paywall.checkout");

  const benefits = [
    t("paywall.benefit1"),
    t("paywall.benefit2"),
    t("paywall.benefit3"),
  ];

  const yearlyAvailable = useRevenueCat
    ? Boolean(rcOffering?.yearly)
    : true;
  const monthlyAvailable = useRevenueCat
    ? Boolean(rcOffering?.monthly)
    : true;

  return (
    <Screen className="gap-2.5 space-y-0">
      <button
        type="button"
        onClick={() => void navigate({ to: "/app/today" })}
        className="text-sm text-foreground-muted"
      >
        {t("paywall.back")}
      </button>

      <div className="shrink-0 space-y-1">
        <Text variant="title" as="h1">
          {hero.title}
        </Text>
        <Text variant="muted" className="line-clamp-2 text-sm leading-snug">
          {hero.lead}
        </Text>
      </div>

      {status?.isPremium ? (
        <Card className="space-y-2 border-primary/20 bg-primary-soft/40 p-4">
          <Text variant="heading" as="h2">
            {t("paywall.youHavePlus")}
          </Text>
          <Text variant="subtle" className="text-sm">
            {status.plan} · {status.status}
          </Text>
          <Button
            fullWidth
            variant="outline"
            onClick={() => void navigate({ to: "/app/explore" })}
          >
            {t("paywall.openExplore")}
          </Button>
        </Card>
      ) : (
        <>
          <ul className="shrink-0 space-y-1.5">
            {benefits.map((b) => (
              <li
                key={b}
                className="flex gap-2 text-sm leading-snug text-foreground-muted"
              >
                <span className="text-primary" aria-hidden>
                  ✓
                </span>
                <span className="line-clamp-2">{b}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2">
            {yearlyAvailable ? (
              <button
                type="button"
                onClick={() => setSelected("plus_yearly")}
                className={`w-full rounded-2xl border p-3 text-left transition ${
                  selected === "plus_yearly"
                    ? "border-primary bg-primary-soft/40 ring-2 ring-primary/30"
                    : "border-border bg-surface-raised"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Text variant="body" className="font-semibold">
                      {t("paywall.yearly")}
                    </Text>
                    <Text variant="caption">{t("paywall.yearlyHint")}</Text>
                  </div>
                  <div className="text-right">
                    <div className="text-base font-semibold">{yearlyLabel}</div>
                    {savePct != null && savePct > 0 ? (
                      <span className="text-[11px] font-semibold text-primary">
                        {t("paywall.savePercent", { pct: savePct })}
                      </span>
                    ) : null}
                  </div>
                </div>
              </button>
            ) : null}

            {monthlyAvailable ? (
              <button
                type="button"
                onClick={() => setSelected("plus_monthly")}
                className={`w-full rounded-2xl border p-3 text-left transition ${
                  selected === "plus_monthly"
                    ? "border-primary bg-primary-soft/40 ring-2 ring-primary/30"
                    : "border-border bg-surface-raised"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Text variant="body" className="font-semibold">
                      {t("paywall.monthly")}
                    </Text>
                    <Text variant="caption">{t("paywall.monthlyHint")}</Text>
                  </div>
                  <div className="text-right text-base font-semibold">
                    {monthlyLabel}
                  </div>
                </div>
              </button>
            ) : null}
          </div>

          <div className="shrink-0 space-y-2">
            {isNative && !useRevenueCat ? (
              <Text variant="caption" className="text-center">
                {t("paywall.rcNotConfigured")}
              </Text>
            ) : null}
            <Button
              fullWidth
              loading={busy}
              disabled={!canPurchase}
              onClick={() => void purchase(selected)}
            >
              {ctaLabel}
            </Button>
            {isNative ? (
              <Button
                fullWidth
                variant="ghost"
                className="min-h-10"
                loading={busy}
                onClick={() => void restore()}
              >
                {t("paywall.restore")}
              </Button>
            ) : null}
            <Text variant="caption" className="line-clamp-2 text-center leading-snug">
              {useRevenueCat
                ? t("paywall.finePrintNative")
                : t("paywall.finePrint")}
            </Text>
          </div>
        </>
      )}

      {message ? (
        <Text variant="caption" className="shrink-0 text-center">
          {message}
        </Text>
      ) : null}
    </Screen>
  );
}
